import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, GraduationCap, Stethoscope, HardHat, UtensilsCrossed, ShieldCheck, ChevronRight, Loader2 } from 'lucide-react';
import { useFetch, Spinner } from '../lib/useFetch';
import { dashboardApi } from '../lib/api';
import { fmtDate } from '../lib/format';

const TYPE_META = {
  formazione: { label: 'Formazione', icon: GraduationCap, color: '#023F80', bg: '#e0e7ff', href: '/formazione' },
  visite:     { label: 'Visite',     icon: Stethoscope,    color: '#15803d', bg: '#dcfce7', href: '/sorveglianza' },
  dpi:        { label: 'DPI',        icon: HardHat,        color: '#b45309', bg: '#fef7d4', href: '/dpi' },
  haccp:      { label: 'HACCP',      icon: UtensilsCrossed,color: '#9333ea', bg: '#f3e8ff', href: '/haccp' },
  sicurezza:  { label: 'Sicurezza',  icon: ShieldCheck,    color: '#b91c1c', bg: '#fee2e2', href: '/sicurezza' },
};

const daysUntil = (iso) => {
  if (!iso) return null;
  const d = new Date(iso + 'T00:00:00Z');
  const today = new Date();
  return Math.round((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
};

const UrgencyTag = ({ days }) => {
  if (days == null) return null;
  let cls = 'bg-emerald-100 text-emerald-700';
  let label = `${days}gg`;
  if (days < 0) { cls = 'bg-red-100 text-red-700'; label = `Scaduto da ${Math.abs(days)}gg`; }
  else if (days <= 7) cls = 'bg-red-100 text-red-700';
  else if (days <= 30) cls = 'bg-amber-100 text-amber-700';
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${cls}`}>{label}</span>;
};

const UpcomingExpirationsWidget = () => {
  const [days, setDays] = useState(30);
  const { data, loading } = useFetch(() => dashboardApi.upcoming(days), [days]);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5" data-testid="upcoming-widget">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#fef7d4] text-[#8a6608] flex items-center justify-center">
            <AlertTriangle size={18} />
          </div>
          <div>
            <h3 className="font-bold text-gray-800 text-sm">Prossime scadenze</h3>
            <p className="text-[11px] text-slate-500">Tutte le scadenze in arrivo nei prossimi giorni</p>
          </div>
        </div>
        <div className="flex gap-1" role="tablist" aria-label="Orizzonte">
          {[30, 60, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`upcoming-days-${d}`}
              className={`px-3 py-1 rounded-full text-xs font-semibold ${days === d ? 'bg-[#023F80] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            >{d} giorni</button>
          ))}
        </div>
      </div>

      {loading ? <div className="py-6"><Spinner /></div> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4" data-testid="upcoming-summary">
            {Object.entries(TYPE_META).map(([key, m]) => {
              const Icon = m.icon;
              const n = data?.by_type?.[key] || 0;
              return (
                <Link
                  key={key}
                  to={m.href}
                  className="rounded-xl p-3 border border-slate-100 hover:border-[#023F80]/30 transition-colors"
                  style={{ background: n > 0 ? m.bg : '#fafafa' }}
                >
                  <div className="flex items-center gap-2">
                    <Icon size={16} style={{ color: m.color }} />
                    <span className="text-[10px] uppercase font-semibold text-slate-600">{m.label}</span>
                  </div>
                  <div className="mt-1.5 text-2xl font-extrabold" style={{ color: m.color }}>{n}</div>
                </Link>
              );
            })}
          </div>

          {(!data?.items || data.items.length === 0) ? (
            <div className="py-10 text-center" data-testid="upcoming-empty">
              <ShieldCheck size={36} className="mx-auto text-emerald-400 mb-2" />
              <p className="text-sm text-slate-500">Nessuna scadenza nei prossimi {days} giorni. Tutto in regola.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100" data-testid="upcoming-list">
              {data.items.slice(0, 8).map((it, i) => {
                const meta = TYPE_META[it.type] || {};
                const Icon = meta.icon;
                return (
                  <Link key={i} to={meta.href || '#'} className="flex items-center gap-3 py-2.5 hover:bg-slate-50 -mx-2 px-2 rounded-lg">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: meta.bg, color: meta.color }}>
                      {Icon && <Icon size={15} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-gray-800 truncate">{it.name}</div>
                      <div className="text-[11px] text-slate-500 truncate">
                        {it.workerName && <span>{it.workerName} · </span>}
                        <span className="text-slate-600">{it.companyName}</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <UrgencyTag days={daysUntil(it.scadenza)} />
                      <span className="text-[11px] text-slate-500">{fmtDate(it.scadenza)}</span>
                    </div>
                    <ChevronRight size={14} className="text-slate-300 hidden sm:block" />
                  </Link>
                );
              })}
              {data.items.length > 8 && (
                <div className="pt-3 text-center">
                  <Link to="/report" className="text-xs font-semibold text-[#023F80] hover:underline">
                    Vedi tutte le {data.total} scadenze →
                  </Link>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UpcomingExpirationsWidget;
